import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Play, PlusCircle } from 'lucide-react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { ScrollBar } from '@/components/ui/scroll-area';
import { GraphSnapshot } from '@/state/graphHistory';

interface Props {
  snapshots: GraphSnapshot[];
  /** Index into ``snapshots``; ``null`` means "live / latest". */
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
  className?: string;
}

function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const diffMs = Date.now() - then;
    const sec = Math.round(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    return `${day}d ago`;
  } catch {
    return iso;
  }
}

/**
 * Timeline scrubber for any graph with a persisted snapshot history.
 * Shows one dot per snapshot; clicking a dot rewinds the graph panel to
 * that point-in-time. The rightmost dot is "live" and always reflects
 * the current ``graphState`` — ``onSelect(null)`` returns the caller to
 * live mode.
 */
const GraphTimeline = memo(function GraphTimeline({
  snapshots,
  selectedIndex,
  onSelect,
  className
}: Props) {
  const latestIndex = snapshots.length - 1;
  const isLive = selectedIndex === null || selectedIndex >= latestIndex;

  const events = useMemo(() => {
    return snapshots.map((snap, i) => ({
      index: i,
      kind: snap.event,
      at: snap.recorded_at,
      label: i === 0 ? 'Initial snapshot' : `Snapshot ${i + 1}`,
      nodeCount: Array.isArray(snap.nodes) ? snap.nodes.length : 0,
      edgeCount: Array.isArray(snap.edges) ? snap.edges.length : 0
    }));
  }, [snapshots]);

  const stripRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isLive || !stripRef.current) return;
    stripRef.current.scrollLeft = stripRef.current.scrollWidth;
  }, [isLive, events.length]);

  const [replayTarget, setReplayTarget] = useState<number | null>(null);
  const isReplaying = replayTarget !== null;

  useEffect(() => {
    if (replayTarget === null) return;
    const current = selectedIndex ?? latestIndex;
    if (current >= replayTarget) {
      setReplayTarget(null);
      return;
    }
    const t = setTimeout(() => {
      const next = current + 1;
      if (next >= latestIndex) onSelect(null);
      else onSelect(next);
    }, 350);
    return () => clearTimeout(t);
  }, [replayTarget, selectedIndex, latestIndex, onSelect]);

  const handleGoLive = () => {
    if (isLive || isReplaying) return;
    setReplayTarget(latestIndex);
  };

  if (snapshots.length < 2) return null;

  const activeIndex =
    selectedIndex === null || selectedIndex >= latestIndex
      ? latestIndex
      : selectedIndex;
  const playheadPct =
    events.length <= 1 ? 100 : (activeIndex / (events.length - 1)) * 100;

  return (
    <div
      className={cn(
        'flex flex-none items-center gap-2 border-t border-border bg-card/70 px-3 py-1.5',
        className
      )}
      role="group"
      aria-label="Graph timeline"
    >
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Clock className="h-3 w-3" aria-hidden="true" />
        <span className="text-[10px] font-semibold uppercase tracking-wide">
          Timeline
        </span>
      </div>
      <TooltipProvider delayDuration={150}>
        <ScrollAreaPrimitive.Root className="relative flex-1 min-w-0 h-7 overflow-hidden">
          <ScrollAreaPrimitive.Viewport
            ref={stripRef}
            className="h-full w-full"
          >
            <div
              className="relative flex h-6 min-w-full items-center"
              style={{ minWidth: events.length * 28 }}
            >
            <div
              className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-border"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute top-1/2 left-0 h-[2px] -translate-y-1/2 rounded-full bg-primary/80 transition-[width] duration-500"
              style={{
                width: `${playheadPct}%`,
                transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)'
              }}
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute top-1/2 z-20 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.25)] transition-[left] duration-500"
              style={{
                left: `${playheadPct}%`,
                transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)'
              }}
              aria-hidden="true"
            />
            <div className="relative flex w-full items-center justify-between">
              {events.map((ev) => {
                const isSelected =
                  selectedIndex === ev.index ||
                  (isLive && ev.index === latestIndex);
                return (
                  <Tooltip key={ev.index}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          onSelect(ev.index === latestIndex ? null : ev.index)
                        }
                        className={cn(
                          'relative z-10 flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full',
                          'transition-all duration-300 ease-out',
                          'hover:scale-125',
                          isSelected
                            ? 'bg-primary scale-110 ring-2 ring-primary/40'
                            : ev.kind === 'create'
                              ? 'bg-muted-foreground/60'
                              : 'bg-muted-foreground/40 hover:bg-primary/70'
                        )}
                        style={{
                          transitionTimingFunction:
                            'cubic-bezier(0.22, 1, 0.36, 1)'
                        }}
                        aria-label={`${ev.label} (${formatRelative(ev.at)})`}
                      >
                        {ev.kind === 'create' ? (
                          <PlusCircle
                            className="h-2.5 w-2.5 text-card"
                            aria-hidden="true"
                          />
                        ) : null}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <div className="font-medium">{ev.label}</div>
                      <div className="text-muted-foreground">
                        {formatRelative(ev.at)} · {ev.nodeCount} nodes ·{' '}
                        {ev.edgeCount} edges
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>
          </ScrollAreaPrimitive.Viewport>
          <ScrollBar orientation="horizontal" />
        </ScrollAreaPrimitive.Root>
      </TooltipProvider>
      <button
        type="button"
        onClick={handleGoLive}
        disabled={isLive || isReplaying}
        className={cn(
          'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          'transition-colors',
          isLive
            ? 'border-primary/50 bg-primary/10 text-primary cursor-default'
            : isReplaying
              ? 'border-primary/40 bg-primary/5 text-primary cursor-wait'
              : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'
        )}
        aria-label="Jump to latest snapshot"
      >
        <Play
          className={cn(
            'h-2.5 w-2.5',
            isReplaying && 'animate-pulse'
          )}
          aria-hidden="true"
        />
        {isLive ? 'Live' : isReplaying ? 'Replay' : 'Go live'}
      </button>
    </div>
  );
});

export default GraphTimeline;
